import {
  CompanyTypeValueDisplayNameMappingDataTransformer,
  EconomicParticipantTypeValueDisplayNameMappingDataTransformer,
  EmployedTypeValueDisplayNameMappingDataTransformer,
  GrossValueAddedTypeValueDisplayNameMappingDataTransformer,
  TouristTypeValueDisplayNameMappingDataTransformer,
} from '../../data_transformers/dashboard_data_transformer';
import {
  DashboardCompanyTypeMetricSingleSelectFilter,
  DashboardEconomicParticipantMetricSingleSelectFilter,
  DashboardEmployedTypeMetricSingleSelectFilter,
  DashboardGrossValueAddedTypeMetricSingleSelectFilter,
  DashboardTouristTypeSingleSelectFilter,
} from '../../filters/dashboard_filter_implementations';
import { Granularity, Metric } from '../dashboard-config.model';
import {
  defineMetricProfile,
  MetricLabels,
  MetricProfile,
  MetricProfileConfig,
} from './metric-profile';

/* ==========================================================================
 * Companies
 * ========================================================================== */

export type CompaniesFilterOptions = ConstructorParameters<
  typeof DashboardCompanyTypeMetricSingleSelectFilter
>[0];

const companiesFilterBase = {
  name: 'metric',
  valueLabelKey: 'company_type_name',
  create: (options?: CompaniesFilterOptions) => [
    new DashboardCompanyTypeMetricSingleSelectFilter(options),
  ],
  overrides: [
    {
      options: {
        excludedOptions: ['small_companies'],
      } satisfies CompaniesFilterOptions,
      granularities: [Granularity.Districts, Granularity.DistrictSubMarkets, Granularity.DistrictWzClasses],
    },
  ],
};

const companiesFilterValueLabelTransformers = {
  create: () => [new CompanyTypeValueDisplayNameMappingDataTransformer()],
};

const createCompaniesProfileConfig = (
  id: Metric.CompaniesCount | Metric.CompaniesRevenue,
  datasetToken: string,
  labels: MetricLabels,
): MetricProfileConfig => ({
  id,
  datasetToken,
  labels,
  filter: { ...companiesFilterBase },
  filterValueLabelTransformers: companiesFilterValueLabelTransformers,
});

export const companiesCountProfile: MetricProfile = defineMetricProfile(
  createCompaniesProfileConfig(Metric.CompaniesCount, 'txd__companies_count', {
    headingPhrase: 'ANZAHL AN',
    shortLabel: 'Anzahl',
    label: 'Unternehmen',
    entityLabel: 'Unternehmen',
    typeFilterTitle: 'Unternehmenstyp',
    displayUnit: 'Unternehmen',
    entity: { structureLabel: 'Unternehmensstruktur', entityAfterDerLabel: 'Unternehmen' },
  }),
);

export const companiesRevenueProfile: MetricProfile = defineMetricProfile(
  createCompaniesProfileConfig(Metric.CompaniesRevenue, 'txd__companies_revenue', {
    headingPhrase: 'UMSÄTZE VON',
    shortLabel: 'Umsatz',
    label: 'Umsätze',
    entityLabel: 'Umsatz',
    typeFilterTitle: 'Unternehmenstyp',
    entity: { structureLabel: 'Umsatzstruktur', entityAfterDerLabel: 'Umsätze' },
  }),
);

/* ==========================================================================
 * Employed
 * ========================================================================== */

export type EmployedFilterOptions = ConstructorParameters<
  typeof DashboardEmployedTypeMetricSingleSelectFilter
>[0];

const employedProfileConfig: MetricProfileConfig = {
  id: Metric.Employed,
  datasetToken: 'emd__count',
  labels: {
    headingPhrase: 'ANZAHL AN',
    shortLabel: 'Anzahl',
    label: 'Beschäftigte',
    countUnit: 'Personen',
    entityLabel: 'Beschäftigte',
    typeFilterTitle: 'Beschäftigten-Kategorie',
    entity: {
      structureLabel: 'Beschäftigtenstruktur',
      entityAfterDerLabel: 'Beschäftigten',
      standaloneEntityLabel: 'Beschäftigte',
    },
  },
  temporal: { kind: 'period', periodKinds: ['year', 'quarter'], yearAsPeriodId: true },
  filter: {
    name: 'employed',
    valueLabelKey: 'employed_type_name',
    create: (options?: EmployedFilterOptions) => [
      new DashboardEmployedTypeMetricSingleSelectFilter(options),
    ],
  },
  filterValueLabelTransformers: {
    create: () => [new EmployedTypeValueDisplayNameMappingDataTransformer()],
  },
};

export const employedProfile: MetricProfile = defineMetricProfile(employedProfileConfig);

/* ==========================================================================
 * Economic participants
 * ========================================================================== */

export type EconomicParticipantFilterOptions = ConstructorParameters<
  typeof DashboardEconomicParticipantMetricSingleSelectFilter
>[0];

const economicParticipantsProfileConfig: MetricProfileConfig = {
  id: Metric.EconomicParticipant,
  datasetToken: 'economic_participants',
  labels: {
    headingPhrase: 'ANZAHL AN',
    shortLabel: 'Anzahl',
    label: 'Erwerbstätige',
    entityLabel: 'Erwerbstätige',
    typeFilterTitle: 'Erwerbstätigen-Kategorie',
    entity: {
      structureLabel: 'Erwerbstätigenstruktur',
      entityAfterDerLabel: 'Erwerbstätigen',
      standaloneEntityLabel: 'Erwerbstätige',
    },
  },
  filter: {
    name: 'economic_participant',
    valueLabelKey: 'economic_participant_type_name',
    create: (options?: EconomicParticipantFilterOptions) => [
      new DashboardEconomicParticipantMetricSingleSelectFilter(options),
    ],
    overrides: [
      {
        options: {
          excludedOptions: ['total', 'marginal'],
        } satisfies EconomicParticipantFilterOptions,
        granularities: [Granularity.Districts, Granularity.DistrictSubMarkets, Granularity.DistrictWzClasses],
      },
    ],
  },
  filterValueLabelTransformers: {
    create: () => [
      new EconomicParticipantTypeValueDisplayNameMappingDataTransformer(),
    ],
  },
};

export const economicParticipantsProfile: MetricProfile = defineMetricProfile(
  economicParticipantsProfileConfig,
);

/* ==========================================================================
 * Gross value added
 * ========================================================================== */

export type GrossValueAddedFilterOptions = ConstructorParameters<
  typeof DashboardGrossValueAddedTypeMetricSingleSelectFilter
>[0];

const grossValueAddedProfileConfig: MetricProfileConfig = {
  id: Metric.GrossValueAdded,
  datasetToken: 'gross_value_added',
  labels: {
    headingPhrase: 'BRUTTOWERTSCHÖPFUNG',
    shortLabel: 'Bruttowertschöpfung',
    label: 'Bruttowertschöpfung',
    entityLabel: 'Bruttowertschöpfung',
    entity: { structureLabel: 'Bruttowertschöpfungsstruktur', entityAfterDerLabel: 'Bruttowertschöpfung' },
  },
  // Bruttowertschöpfung / Produktionswert: the option is the metric itself.
  filter: {
    name: 'gross_value_added',
    valueLabelKey: 'gross_value_added_type_name',
    selectsMetric: true,
    create: (options?: GrossValueAddedFilterOptions) => [
      new DashboardGrossValueAddedTypeMetricSingleSelectFilter(options),
    ],
  },
  filterValueLabelTransformers: {
    create: () => [new GrossValueAddedTypeValueDisplayNameMappingDataTransformer()],
  },
};

export const gvaProfile: MetricProfile = defineMetricProfile(grossValueAddedProfileConfig);

/* ==========================================================================
 * Self-employed
 * ========================================================================== */

// Single KPI (solo_self_employed_count), so no type filter.
export const selfEmployedProfile: MetricProfile = defineMetricProfile({
  id: Metric.SelfEmployed,
  datasetToken: 'sse__count',
  labels: {
    headingPhrase: 'ANZAHL AN SOLO-SELBSTSTÄNDIGEN',
    shortLabel: 'Anzahl',
    label: 'Solo-Selbstständige',
    entityLabel: 'Solo-Selbstständige',
  },
});

/* ==========================================================================
 * Tourism
 * ========================================================================== */

export const tourismProfile: MetricProfile = defineMetricProfile({
  id: Metric.Tourism,
  datasetToken: 'tourism_accommodations',
  labels: {
    headingPhrase: 'ANZAHL AN TOURISM',
    shortLabel: 'Anzahl',
    typeFilterTitle: 'Touristentyp',
  },
  temporal: { kind: 'period', periodKinds: ['year', 'quarter', 'month'], maxYearOffset: -1 },
  filter: {
    name: 'tourists',
    valueLabelKey: 'tourist_type_type_name',
    create: () => [new DashboardTouristTypeSingleSelectFilter()],
  },
  filterValueLabelTransformers: {
    create: () => [new TouristTypeValueDisplayNameMappingDataTransformer()],
  },
});

/* ==========================================================================
 * Registry
 * ========================================================================== */

export const METRIC_PROFILES: Record<Metric, MetricProfile> = {
  [Metric.CompaniesCount]: companiesCountProfile,
  [Metric.CompaniesRevenue]: companiesRevenueProfile,
  [Metric.GrossValueAdded]: gvaProfile,
  [Metric.EconomicParticipant]: economicParticipantsProfile,
  [Metric.Employed]: employedProfile,
  [Metric.SelfEmployed]: selfEmployedProfile,
  [Metric.Tourism]: tourismProfile,
};
